import { appwriteConfig, databaseId, databases } from '../config/appwrite';
import { DachthekenduettConfig } from '../types';

const CONFIG_DOC_ID = 'global';
const WEBHOOK_URL = 'https://n8n.koll.cc/webhook/df46fc98-fcbf-441f-943c-5451bdc327b9';
const DEFAULT_PROMPT = 'Bitte analysiere das Transkript und liefere mehrere klar getrennte Textbausteine als JSON-Array.';

const getDefaultConfig = (): DachthekenduettConfig => ({
  prompt: DEFAULT_PROMPT
});

export interface DachthekenduettResultItem {
  label: string;
  value: string;
}

const stringifyItem = (item: unknown): string => {
  if (typeof item === 'string') {
    return item;
  }

  if (item && typeof item === 'object') {
    const maybeText = (item as { text?: unknown }).text;
    const maybeContent = (item as { content?: unknown }).content;
    const maybeOutput = (item as { output?: unknown }).output;
    const maybeValue = (item as { value?: unknown }).value;

    const firstMatch = [maybeText, maybeContent, maybeOutput, maybeValue].find(
      (value) => typeof value === 'string' && value.trim().length > 0
    );

    if (typeof firstMatch === 'string') {
      return firstMatch;
    }

    return JSON.stringify(item, null, 2);
  }

  if (item === null || item === undefined) {
    return '';
  }

  return String(item);
};

const toLabel = (key: string): string => {
  if (!key) return 'Ergebnis';
  return key.charAt(0).toUpperCase() + key.slice(1);
};

const normalizeWebhookResponse = (data: unknown): DachthekenduettResultItem[] => {
  let result: DachthekenduettResultItem[] = [];

  if (Array.isArray(data)) {
    result = data
      .map((item, index) => ({
        label: `Element ${index + 1}`,
        value: stringifyItem(item)
      }))
      .filter((item) => item.value.length > 0);
  } else if (data && typeof data === 'object') {
    result = Object.entries(data as Record<string, unknown>)
      .map(([key, rawValue]) => ({
        label: toLabel(key),
        value: stringifyItem(rawValue)
      }))
      .filter((item) => item.value.trim().length > 0);
  } else {
    result = [
      {
        label: 'Ergebnis',
        value: stringifyItem(data)
      }
    ].filter((item) => item.value.length > 0);
  }

  if (result.length === 0) {
    throw new Error('Webhook hat keine nutzbaren Textelemente geliefert');
  }

  return result;
};

export class DachthekenduettService {
  static async getConfig(): Promise<DachthekenduettConfig> {
    try {
      const doc = await databases.getDocument(databaseId, appwriteConfig.collections.dachthekenduettConfigs, CONFIG_DOC_ID);
      const prompt = doc.prompt ?? doc.data?.prompt;
      return { prompt: typeof prompt === 'string' && prompt.trim() ? prompt : DEFAULT_PROMPT };
    } catch (error: any) {
      if (error?.code === 404 && error?.type === 'document_not_found') return getDefaultConfig();
      throw error;
    }
  }

  static async saveConfig(prompt: string, userId: string): Promise<void> {
    const cleanPrompt = prompt.trim();
    if (!cleanPrompt) throw new Error('Der Schreibauftrag darf nicht leer sein.');
    const data = { prompt: cleanPrompt, updatedBy: userId };
    try {
      await databases.updateDocument(databaseId, appwriteConfig.collections.dachthekenduettConfigs, CONFIG_DOC_ID, data);
    } catch (error: any) {
      if (error?.code !== 404 || error?.type !== 'document_not_found') throw error;
      await databases.createDocument(databaseId, appwriteConfig.collections.dachthekenduettConfigs, CONFIG_DOC_ID, data);
    }
    const saved = await this.getConfig();
    if (saved.prompt !== cleanPrompt) throw new Error('Speichern konnte nicht bestätigt werden. Bitte versuche es erneut.');
  }

  static async processTranscript(transcript: string, prompt: string): Promise<DachthekenduettResultItem[]> {
    const cleanTranscript = transcript.trim();
    const cleanPrompt = prompt.trim();

    if (!cleanTranscript) {
      throw new Error('Transkript darf nicht leer sein');
    }
    if (!cleanPrompt) {
      throw new Error('Prompt darf nicht leer sein');
    }

    const response = await fetch(WEBHOOK_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        transcript: cleanTranscript,
        prompt: cleanPrompt
      })
    });

    if (!response.ok) {
      throw new Error(`Webhook-Fehler (${response.status})`);
    }

    const data = await response.json();
    return normalizeWebhookResponse(data);
  }
}
